import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';

import type { EmployeeOptionVM } from 'ab-viewModels/employee/extendedOption.viewModel';
import type EquipmentViewModel from 'ab-viewModels/equipment.viewModel';
import type TemporaryEmployeeOptionVM from 'ab-viewModels/temporaryEmployee/temporaryEmployeeOption.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import type { AssignableResourceType } from '@acceligentllc/shared/enums/assignableResourceType';
import type { WorkOrderPosition } from '@acceligentllc/shared/enums/workOrderPosition';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import { WORK_ORDER_FORM } from 'af-constants/reduxForms';

import ResourceAssignConfirmationModal from 'af-components/SharedForms/ResourceAssignModal';

import * as WorkOrderActions from 'af-actions/workOrder';

import DirectoriesAndAttachments from './Attachments';
import type WorkOrderFM from '../formModel';
import type { AddressArrayFM } from '../formModel';
import Customer from './Customer';
import DeliverableData from './DeliverableData';
import General from './General';
import Notes from './Notes';
import Notifications from './Notifications';
import Resources from './ResourceLookups';
import ScheduleBoard from './ScheduleBoard';
import SiteContact from './SiteContact';
import styles from './styles.module.scss';
import WorkLocation from './WorkLocation';
import Section from '../Shared/Section';

interface OwnProps {
	employees: EmployeeOptionVM[];
	equipment: EquipmentViewModel[];
	temporaryEmployees: TemporaryEmployeeOptionVM[];
	initialIndex: Nullable<number>;
	loadEmployees: (isLazyLoaded: boolean) => void;
	loadEquipment: (isLazyLoaded: boolean) => void;
	loadTemporaryEmployees: (isLazyLoaded: boolean) => void;
	resetTimer: () => void;
	change: <T extends keyof WorkOrderFM>(fieldName: T, value: WorkOrderFM[T]) => void;
	selector: (fieldName: string) => AddressArrayFM;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const WorkOrderForm: React.FC<Props> = (props) => {
	const {
		resetTimer,
		disabled,
		isAllowedToViewContacts,
		employees,
		equipment,
		temporaryEmployees,
		isDeliverable,
		loadEmployees,
		loadEquipment,
		loadTemporaryEmployees,
		addresses,
		orderPosition,
		dueDate,
		notificationsEnabled,
		loadBlankPositions,
		initialIndex,
		change,
		selector,
		isCancelled,
		attachmentsLocked,
		jobTimezone,
		companyTimezone,
		hasJob,
	} = props;

	const [resourceAssignModalType, setResourceAssignModalType] = React.useState<Nullable<AssignableResourceType>>(null);

	const openResourceAssignConfirmationModal = React.useCallback((resourceType: AssignableResourceType) => {
		setResourceAssignModalType(resourceType);
	}, []);

	const closeResourceAssignConfirmationModal = React.useCallback(() => {
		setResourceAssignModalType(null);
	}, []);

	if (!hasJob) {
		return null;
	}

	return (
		<div className={styles.form}>
			<General
				change={change}
				disabled={disabled}
				resetTimer={resetTimer}
			/>
			<ScheduleBoard
				change={change}
				disabled={disabled}
				dueDate={dueDate}
				initialIndex={initialIndex}
				loadBlankPositions={loadBlankPositions}
				orderPosition={orderPosition}
				resetTimer={resetTimer}
			/>
			{notificationsEnabled && <Notifications disabled={disabled} />}
			<WorkLocation
				addresses={addresses}
				change={change}
				companyTimezone={companyTimezone}
				disabled={disabled}
				jobTimezone={jobTimezone}
				resetTimer={resetTimer}
				selector={selector}
			/>
			<DeliverableData
				disabled={disabled}
				isDeliverableJob={isDeliverable}
			/>
			<Customer disabled={disabled} />
			{isAllowedToViewContacts && <SiteContact disabled={disabled} resetTimer={resetTimer} />}
			<Notes disabled={disabled} resetTimer={resetTimer} />
			<Resources
				change={change}
				disabled={disabled}
				employees={employees}
				equipment={equipment}
				isCancelled={isCancelled}
				loadEmployees={loadEmployees}
				loadEquipment={loadEquipment}
				loadTemporaryEmployees={loadTemporaryEmployees}
				resetTimer={resetTimer}
				showResourceAssignConfirmationModal={openResourceAssignConfirmationModal}
				temporaryEmployees={temporaryEmployees}
			/>
			{hasJob &&
				<Section
					label="Attachments"
					tooltip={`
						Work Order related attachments. Field people will have access to all of them.
						WOs are initialized with job-level attachments that have been marked to copy to WO.
						Changing job after WO creation will affect attachments as well.
					`}
				>
					<DirectoriesAndAttachments change={change} isReadOnly={attachmentsLocked} />
				</Section>
			}
			<ResourceAssignConfirmationModal
				onClose={closeResourceAssignConfirmationModal}
				onSubmit={closeResourceAssignConfirmationModal}
				showCancel={false}
				showModal={!!resourceAssignModalType}
				type={resourceAssignModalType}
			/>
		</div >
	);
};

const selector = formValueSelector(WORK_ORDER_FORM);

function mapStateToProps(state: RootState) {
	const { user: { userData, companyData }, company: { company }, workOrder: { order } } = state;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const {
		status,
		job,
		dueDate,
		addresses,
		position,
		isInternal,
		id: workOrderId,
		jobTimezone,
	} = selector(state, 'status', 'job', 'dueDate', 'addresses', 'position', 'isInternal', 'id', 'jobTimezone') as WorkOrderFM;

	const isAllowedToViewProdData = isAllowed(
		PagePermissions.COMPANY.PROD_DATA.VIEW,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	const isAllowedToViewContacts = isAllowed(
		PagePermissions.COMPANY.CONTACTS,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	return {
		hasJob: !!job?.id,
		jobTimezone,
		companyTimezone: company?.timezone ?? null,
		disabled: status === WorkOrderStatus.CANCELED || status === WorkOrderStatus.LOCKED || !job?.id || !dueDate || (order?.isPaused ?? false),
		isAllowedToViewProdData,
		isAllowedToViewContacts,
		isCancelled: status === WorkOrderStatus.CANCELED,
		isDeliverable: !!job?.isDeliverable,
		addresses,
		orderPosition: position as WorkOrderPosition,
		dueDate: dueDate as string,
		isInternal: !!isInternal,
		notificationsEnabled: !!company?.notification?.isEnabled ?? false,
		workOrderId,
		attachmentsLocked: status === WorkOrderStatus.CANCELED || status === WorkOrderStatus.LOCKED,
	};
}

function mapDispatchToProps() {
	return {
		loadBlankPositions: WorkOrderActions.findBlankWorkOrdersByDueDate,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(WorkOrderForm);
